/* eslint-disable */
import { useEffect, useRef, useState, useCallback } from "react";

function toTVSymbol(ticker) {
  if (!ticker) return null;
  const t = ticker.toUpperCase();
  const cryptoMap = {
    "BTC-USD":  "BINANCE:BTCUSDT",
    "ETH-USD":  "BINANCE:ETHUSDT",
    "SOL-USD":  "BINANCE:SOLUSDT",
    "BNB-USD":  "BINANCE:BNBUSDT",
    "XRP-USD":  "BINANCE:XRPUSDT",
    "AVAX-USD": "BINANCE:AVAXUSDT",
    "DOGE-USD": "BINANCE:DOGEUSDT",
    "ADA-USD":  "BINANCE:ADAUSDT",
    "MATIC-USD":"BINANCE:MATICUSDT",
    "LINK-USD": "BINANCE:LINKUSDT",
    "RUNE-USD": "BINANCE:RUNEUSDT",
    "MSTR":     "NASDAQ:MSTR",
    "COIN":     "NASDAQ:COIN",
    "MARA":     "NASDAQ:MARA",
    "PLTR":     "NASDAQ:PLTR",
  };
  if (cryptoMap[t]) return cryptoMap[t];
  if (t.endsWith("-USD")) return `BINANCE:${t.replace("-USD", "")}USDT`;
  return `NASDAQ:${t}`;
}

function initWidget(container, symbol, height) {
  container.innerHTML = "";
  const script = document.createElement("script");
  script.src = "https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js";
  script.type = "text/javascript";
  script.async = true;
  script.innerHTML = JSON.stringify({
    autosize: false,
    width: "100%",
    height: height,
    symbol,
    interval: "D",
    timezone: "Etc/UTC",
    theme: "dark",
    style: "1",
    locale: "en",
    backgroundColor: "#0a0a0a",
    gridColor: "#1c1c1c",
    hide_top_toolbar: false,
    hide_legend: false,
    allow_symbol_change: false,
    save_image: true,
    calendar: false,
    hide_volume: false,
    support_host: "https://www.tradingview.com",
  });
  container.appendChild(script);
}

const MIN_HEIGHT = 200;
const MAX_HEIGHT = 1200;
const HANDLE_H = 10;

export default function PriceHistoryChart({ ticker, isMobile = false }) {
  const containerRef = useRef(null);
  const symbol = toTVSymbol(ticker);
  const defaultHeight = isMobile ? 400 : 500;

  const storageKey = "tv_chart_height";
  const [chartHeight, setChartHeight] = useState(() => {
    try { const s = localStorage.getItem(storageKey); return s ? parseInt(s) : defaultHeight; }
    catch { return defaultHeight; }
  });

  const dragging = useRef(false);
  const startY = useRef(0);
  const startH = useRef(0);
  const [isDragging, setIsDragging] = useState(false);

  // Re-init widget when height settles after drag ends
  const timerRef = useRef(null);
  const reinit = useCallback((h) => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      if (containerRef.current && symbol) initWidget(containerRef.current, symbol, h);
    }, 300);
  }, [symbol]);

  // Initial load
  useEffect(() => {
    if (!containerRef.current || !symbol) return;
    const timer = setTimeout(() => {
      if (containerRef.current) initWidget(containerRef.current, symbol, chartHeight);
    }, 400);
    return () => {
      clearTimeout(timer);
      if (containerRef.current) containerRef.current.innerHTML = "";
    };
  }, [symbol]);

  // Drag handlers
  const onDragStart = useCallback((clientY) => {
    dragging.current = true;
    startY.current = clientY;
    startH.current = chartHeight;
    setIsDragging(true);
  }, [chartHeight]);

  useEffect(() => {
    const onMove = (e) => {
      if (!dragging.current) return;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      const delta = clientY - startY.current;
      const newH = Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, startH.current + delta));
      setChartHeight(newH);
    };
    const onUp = () => {
      if (!dragging.current) return;
      dragging.current = false;
      setIsDragging(false);
      setChartHeight(h => {
        try { localStorage.setItem(storageKey, String(h)); } catch {}
        reinit(h);
        return h;
      });
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
  }, [reinit]);

  if (!symbol) {
    return (
      <div style={{
        height: chartHeight,
        display: "flex", alignItems: "center", justifyContent: "center",
        flexDirection: "column", gap: 12, textAlign: "center", padding: 24,
        border: "1px solid var(--border)", borderRadius: 8, background: "var(--bg3)",
      }}>
        <i className="ph ph-chart-line-down" style={{ fontSize: 32, color: "var(--text5)" }}/>
        <div>
          <div style={{ fontFamily: "Montserrat,sans-serif", fontSize: 12, fontWeight: 700,
            color: "var(--text3)", letterSpacing: "0.05em", marginBottom: 6 }}>
            No price history available
          </div>
          <div style={{ fontFamily: "Montserrat,sans-serif", fontSize: 10,
            color: "var(--text5)", lineHeight: 1.6 }}>
            This asset doesn't have a known market ticker.<br/>
            Price history is only available for stocks and crypto<br/>
            traded on public exchanges.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: "Montserrat,sans-serif", userSelect: isDragging ? "none" : "auto" }}>
      <div
        className="tradingview-widget-container"
        ref={containerRef}
        style={{
          height: chartHeight,
          borderRadius: 8,
          overflow: "hidden",
          border: "1px solid var(--border)",
          pointerEvents: isDragging ? "none" : "auto",
        }}
      />

      {/* Drag handle */}
      <div
        onMouseDown={e => { e.preventDefault(); onDragStart(e.clientY); }}
        onTouchStart={e => { onDragStart(e.touches[0].clientY); }}
        style={{
          height: HANDLE_H,
          cursor: "ns-resize",
          display: "flex", alignItems: "center", justifyContent: "center",
          margin: "0",
          borderRadius: "0 0 8px 8px",
          background: isDragging ? "rgba(242,135,5,0.15)" : "var(--bg3)",
          border: "1px solid var(--border)",
          borderTop: "none",
          transition: "background 0.15s",
        }}
      >
        <div style={{
          width: 32, height: 3, borderRadius: 2,
          background: isDragging ? "#F28705" : "var(--border2)",
          transition: "background 0.15s",
        }}/>
      </div>

      <div style={{ fontSize: 8, color: "var(--text5)", marginTop: 4,
        textAlign: "right", letterSpacing: "0.1em", fontFamily: "Montserrat,sans-serif" }}>
        Chart by TradingView
      </div>
    </div>
  );
}
